// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * XngCarousel — a thin React wrapper around Embla that adds:
 *
 *  - declarative props for the common controls (arrows, pagination, scrollbar)
 *  - responsive `slidesPerView` configuration
 *  - optional 2D grid layout (`subGridRows`)
 *  - optional autoplay and auto-height plugins
 *  - inline ("overlay") or outer ("below") controls placement
 *
 * The component is intentionally lightweight: rendering of each subcomponent
 * is delegated, and Embla state is fanned out through `CarouselContext`.
 *
 * @example
 *   <XngCarousel slidesPerView={3} spaceBetween={20} navigation pagination>
 *       {items.map(it => <Card key={it.id} {...it} />)}
 *   </XngCarousel>
 */

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ArrowNavigation from "@vanmeter/App/Components/XngCarousel/ArrowNavigation";
import { CarouselProvider } from "@vanmeter/App/Components/XngCarousel/CarouselContext";
import CarouselControls from "@vanmeter/App/Components/XngCarousel/CarouselControls";
import { useCarousel } from "@vanmeter/App/Components/XngCarousel/hooks";
import { XngCarouselProps } from "@vanmeter/App/Components/XngCarousel/models";
import {
    CarouselContainer,
    CarouselLayoutProps,
    CarouselWrapper,
    SlideItem,
    SlidesContainer,
} from "@vanmeter/App/Components/XngCarousel/styled";
import { xCarouselStyle } from "@vanmeter/App/Components/XngCarousel/styles";
import { CustomAppState } from "@vanmeter/App/Store/Reducers";
import { mergeWithCss } from "@vanmeter/XDL/Utilities/mergeWithCss";
import { EmblaCarouselType, EmblaOptionsType, EmblaPluginType } from "embla-carousel";
import AutoHeight from "embla-carousel-auto-height";
import Autoplay from "embla-carousel-autoplay";
import debounce from "lodash/debounce";
import React, { FC, useEffect, useId, useMemo } from "react";
import { css } from "styled-components";

// ─── Style Types ─────────────────────────────────────────────────────────────────
export interface CarouselStyle {
    container?: InjectableCss;
}

// ─── Defaults ─────────────────────────────────────────────────────────────────

const DEFAULT_AUTOPLAY_DELAY_MS = 4000;
const DEFAULT_SPACE_BETWEEN_PX = 20;

// ─── Carousel styles ──────────────────────────────────────────────────────────

/**
 * Default styles for the carousel wrapper, layered on top of `xCarouselStyle`.
 */
export const carouselStyles: CarouselStyle = {
    container: {
        css: xCarouselStyle,
    },
};

// ─── Inner Component ──────────────────────────────────────────────────────────

const XngCarouselInner: FC<XngCarouselProps> = ({
    children,
    className = "",
    extendedStyles,
    height = "480px",
    navigation = true,
    spaceBetween = DEFAULT_SPACE_BETWEEN_PX,
    navigationOutside = false,
    pagination = false,
    scrollbar = false,
    updateOnWindowResize = true,
    onInit,
    subGridRows,
    slidesPerView,
    autoHeight = false,
    autoplay = false,
    loop = false,
    options: optionsProp,
    inlineNavigation = false,
    controlNavigationOutside = false,
    showControls = true,
    direction = "horizontal",
    pausable = false,
}) => {
    const styles = useMemo(() => mergeWithCss(carouselStyles, extendedStyles), [extendedStyles]);

    const plugins: EmblaPluginType[] = useMemo(() => {
        const list: EmblaPluginType[] = [];
        if (autoHeight) list.push(AutoHeight());
        if (autoplay) {
            // XNG-Changes: Pause autoplay while hovering/dragging
            list.push(
                Autoplay({
                    delay: DEFAULT_AUTOPLAY_DELAY_MS,
                    stopOnInteraction: false,
                    stopOnMouseEnter: true,
                    stopOnFocusIn: false,
                    ...(typeof autoplay === "object" ? autoplay : {}),
                }),
            );
        }
        return list;
    }, [autoHeight, autoplay]);

    const { isTabletScreen } = useAppSelector((state: ApplicationState) => ({
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
    }));

    const options: EmblaOptionsType = useMemo(
        () => ({
            loop,
            axis: direction === "vertical" ? "y" : "x",
            dragFree: slidesPerView !== 1 && !isTabletScreen,
            align: "start",
            // XNG-Changes: Prevent dragging when everything already fits (nothing to scroll).
            watchDrag: (api: EmblaCarouselType) => api.canScrollPrev() || api.canScrollNext(),
            ...optionsProp,
        }),
        [loop, direction, slidesPerView, optionsProp, isTabletScreen],
    );

    const { carouselRef, carouselApi, contextValue } = useCarousel(options, plugins);
    const { canScroll } = contextValue;

    const paginationOptions = pagination
        ? typeof pagination === "boolean"
            ? { clickable: true }
            : pagination
        : undefined;

    const scrollbarOptions = scrollbar ? (typeof scrollbar === "boolean" ? { draggable: true } : scrollbar) : undefined;

    const showPagination = Boolean(paginationOptions);
    const showScrollbar = Boolean(scrollbarOptions);
    const isPaginationClickable = paginationOptions?.clickable ?? true;
    const isScrollbarDraggable = scrollbarOptions?.draggable ?? false;

    const shouldHideControls = !canScroll || React.Children.count(children) <= 1;
    const areControlsVisible = showControls && !shouldHideControls && (navigation || showPagination || showScrollbar);

    const layoutProps: CarouselLayoutProps = {
        rows: subGridRows,
        view: subGridRows ? "grid" : "flex",
        loop: options.loop,
        slidesPerView,
        spaceBetween,
        vertical: direction === "vertical",
        height,
    };

    useEffect(() => {
        if (!updateOnWindowResize || !carouselApi) return;

        // Debounce so a continuous drag-resize doesn't trigger a full Embla
        // re-init on every resize event (Embla's own `watchResize` already
        // handles element-size changes promptly; this is the window-level net).
        const handleResize = debounce(() => carouselApi.reInit(), 150);
        window.addEventListener("resize", handleResize);
        return () => {
            handleResize.cancel();
            window.removeEventListener("resize", handleResize);
        };
    }, [carouselApi, updateOnWindowResize]);

    useEffect(() => {
        if (carouselApi && onInit) onInit(carouselApi, carouselRef);
    }, [carouselApi, onInit, carouselRef]);

    const renderControls = (inline: boolean) => (
        <CarouselControls
            showNavigation={navigation}
            showPagination={showPagination}
            paginationClickable={isPaginationClickable}
            showScrollbar={showScrollbar}
            scrollbarDraggable={isScrollbarDraggable}
            scrollbarMode={"percentage"}
            inline={inline}
            navigationOutside={navigationOutside || direction === "vertical"}
            controlNavigationOutside={controlNavigationOutside}
            pausable={pausable}
        />
    );

    const renderSlide = (slide: React.ReactNode, key: number) => {
        if (subGridRows) {
            return <React.Fragment key={key}>{slide}</React.Fragment>;
        }
        return (
            <SlideItem key={key} {...layoutProps} inlineNavigation={inlineNavigation}>
                {slide}
                {areControlsVisible && inlineNavigation && renderControls(true)}
            </SlideItem>
        );
    };

    return (
        <CarouselProvider value={contextValue}>
            <StyledWrapper
                {...styles.container}
                className={`carousel-main-wrapper ${className}`}
                role="region"
                aria-label="Carousel"
            >
                <CarouselWrapper>
                    {areControlsVisible &&
                        (navigationOutside || direction === "vertical" || !inlineNavigation) &&
                        navigation && (
                            <ArrowNavigation arrowType="prev" showOnMobile={false} navigationOutside={true} />
                        )}
                    <CarouselContainer ref={carouselRef} aria-roledescription="carousel" {...layoutProps}>
                        <SlidesContainer {...layoutProps} className="carousel-slides-container">
                            {React.Children.map(children, renderSlide)}
                        </SlidesContainer>
                    </CarouselContainer>
                    {areControlsVisible &&
                        (navigationOutside || direction === "vertical" || !inlineNavigation) &&
                        navigation && (
                            <ArrowNavigation arrowType="next" showOnMobile={false} navigationOutside={true} />
                        )}
                </CarouselWrapper>

                {areControlsVisible && !inlineNavigation && renderControls(false)}
            </StyledWrapper>
        </CarouselProvider>
    );
};

// ─── Location-keyed wrapper (vanmeter-specific) ───────────────────────────────

/**
 * Wraps the inner carousel with a location-derived key so it fully re-mounts
 * whenever the page URL changes. This prevents stale Embla state across
 * client-side navigations.
 */
const XngCarousel: FC<XngCarouselProps> = props => {
    const location = useAppSelector(o => getLocation(o));
    const carouselId = useId();
    const locationKey = `${location?.pathname}${location?.search ?? ""}`;

    return <XngCarouselInner key={`carousel-${locationKey}-${carouselId}`} {...props} />;
};

export default XngCarousel;
